import { useState } from 'react';
import { Search, Loader2, ChevronDown, ChevronUp, Sparkles, Lightbulb, Quote } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AskSpEdKit = ({ variant = 'full', className = '' }) => {
  const [askSpEdKitQuery, setAskSpEdKitQuery] = useState('');
  const [askSpEdKitResponse, setAskSpEdKitResponse] = useState(null);
  const [askSpEdKitLoading, setAskSpEdKitLoading] = useState(false);
  const [showFullAnswer, setShowFullAnswer] = useState(false);
  const [askSpEdKitError, setAskSpEdKitError] = useState(null);

  const handleAskSpEdKit = async (e) => {
    e.preventDefault();
    if (!askSpEdKitQuery.trim()) return;
    
    setAskSpEdKitLoading(true);
    setAskSpEdKitError(null);
    setAskSpEdKitResponse(null);
    setShowFullAnswer(false);
    
    try {
      const response = await axios.post(`${API}/ask-spedkit`, {
        question: askSpEdKitQuery.trim()
      }, { withCredentials: true });
      setAskSpEdKitResponse(response.data);
    } catch (error) {
      console.error('Ask SpEdKit error:', error);
      setAskSpEdKitError('Unable to process your question. Please try again.');
    } finally {
      setAskSpEdKitLoading(false);
    }
  };

  const sampleQuestions = [
    "What are evidence-based strategies for supporting students with ADHD?",
    "How can I implement UDL in a math classroom?",
    "What does research say about social stories for autism?",
    "Best practices for writing measurable IEP goals?"
  ];

  const clearResponse = () => {
    setAskSpEdKitResponse(null);
    setAskSpEdKitQuery('');
    setAskSpEdKitError(null);
  };

  // Compact variant for dashboard
  if (variant === 'compact') {
    return (
      <div className={`bg-gradient-to-br from-[#6366F1]/5 to-[#8B5CF6]/10 rounded-2xl p-6 ${className}`} data-testid="ask-spedkit-compact">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#6366F1]/10 rounded-xl flex items-center justify-center">
            <Search className="w-5 h-5 text-[#6366F1]" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-lg text-text-primary">Ask SpEdKit</h3>
            <p className="font-body text-text-secondary text-sm">Research-backed answers</p>
          </div>
        </div>
        
        <form onSubmit={handleAskSpEdKit} className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <Input
              type="text"
              value={askSpEdKitQuery}
              onChange={(e) => setAskSpEdKitQuery(e.target.value)}
              placeholder="Ask any special education question..."
              className="w-full pl-10 pr-3 py-2 text-sm border border-[#6366F1]/20 rounded-xl focus:border-[#6366F1] focus:ring-[#6366F1]/20 bg-white"
              data-testid="ask-spedkit-input-compact"
              disabled={askSpEdKitLoading}
            />
          </div>
          <Button
            type="submit"
            disabled={askSpEdKitLoading || !askSpEdKitQuery.trim()}
            className="bg-[#6366F1] text-white hover:bg-[#6366F1]/90 rounded-xl px-4 text-sm font-medium"
            data-testid="ask-spedkit-submit-compact"
          >
            {askSpEdKitLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ask'}
          </Button>
        </form>

        {/* Error State */}
        {askSpEdKitError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-xl mt-4 text-sm">
            {askSpEdKitError}
          </div>
        )}

        {/* Loading State */}
        {askSpEdKitLoading && (
          <div className="bg-white rounded-xl p-4 mt-4 text-center border border-[#6366F1]/10">
            <Loader2 className="w-6 h-6 animate-spin text-[#6366F1] mx-auto mb-2" />
            <p className="text-text-secondary text-sm">Researching...</p>
          </div>
        )}

        {/* Compact Response */}
        {askSpEdKitResponse && (
          <div className="bg-white rounded-xl mt-4 border border-[#6366F1]/10 overflow-hidden" data-testid="ask-spedkit-response-compact">
            <div className="bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-white" />
                <span className="font-medium text-white text-sm">SpEdKit&apos;s Answer</span>
              </div>
              <button
                onClick={clearResponse}
                className="text-white/70 hover:text-white text-xs"
              >
                Clear
              </button>
            </div>
            <div className="p-4">
              <div className={`font-body text-text-primary text-sm leading-relaxed ${!showFullAnswer ? 'line-clamp-3' : ''}`}>
                {askSpEdKitResponse.response?.answer || 'No answer available'}
              </div>
              {askSpEdKitResponse.response?.answer?.length > 200 && (
                <button
                  onClick={() => setShowFullAnswer(!showFullAnswer)}
                  className="text-[#6366F1] font-medium text-xs mt-2 flex items-center gap-1 hover:underline"
                >
                  {showFullAnswer ? (
                    <>Less <ChevronUp className="w-3 h-3" /></>
                  ) : (
                    <>More <ChevronDown className="w-3 h-3" /></>
                  )}
                </button>
              )}
              
              {/* Key Points (compact) */}
              {showFullAnswer && askSpEdKitResponse.response?.key_points?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <h4 className="font-medium text-text-primary text-xs mb-2">Key Points</h4>
                  <ul className="space-y-1">
                    {askSpEdKitResponse.response.key_points.slice(0, 3).map((point, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-text-secondary">
                        <div className="w-1.5 h-1.5 bg-[#6366F1] rounded-full mt-1.5 flex-shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sample questions (when no response) */}
        {!askSpEdKitResponse && !askSpEdKitLoading && (
          <div className="flex flex-wrap gap-1.5 mt-4">
            {sampleQuestions.slice(0, 2).map((q, i) => (
              <button
                key={i}
                onClick={() => setAskSpEdKitQuery(q)}
                className="text-xs bg-white border border-[#6366F1]/20 text-text-secondary hover:border-[#6366F1]/50 hover:text-[#6366F1] px-2.5 py-1 rounded-full transition-all"
                data-testid={`sample-question-compact-${i}`}
              >
                {q.length > 40 ? q.substring(0, 40) + '...' : q}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Full variant (default) - for landing page
  return (
    <div className={className} data-testid="ask-spedkit-full">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-[#6366F1]/10 text-[#6366F1] px-4 py-2 rounded-full text-sm font-medium mb-4">
          <Search className="w-4 h-4" />
          <span>Research-Backed Answers</span>
        </div>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-3">
          Ask SpEdKit
        </h2>
        <p className="font-body text-text-secondary max-w-2xl mx-auto">
          Get instant, research-backed answers to your special education questions — cited from leading educational and disability journals.
        </p>
      </div>
      
      {/* Search Input */}
      <form onSubmit={handleAskSpEdKit} className="relative mb-6">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <Input
              type="text"
              value={askSpEdKitQuery}
              onChange={(e) => setAskSpEdKitQuery(e.target.value)}
              placeholder="Ask any special education question..."
              className="w-full pl-12 pr-4 py-4 text-lg border-2 border-[#6366F1]/20 rounded-2xl focus:border-[#6366F1] focus:ring-[#6366F1]/20 bg-white"
              data-testid="ask-spedkit-input"
              disabled={askSpEdKitLoading}
            />
          </div>
          <Button
            type="submit"
            disabled={askSpEdKitLoading || !askSpEdKitQuery.trim()}
            className="bg-[#6366F1] text-white hover:bg-[#6366F1]/90 rounded-2xl px-8 py-4 text-lg font-semibold shadow-lg transition-all"
            data-testid="ask-spedkit-submit"
          >
            {askSpEdKitLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Ask'
            )}
          </Button>
        </div>
      </form>
      
      {/* Sample Questions */}
      {!askSpEdKitResponse && !askSpEdKitLoading && (
        <div className="flex flex-wrap gap-2 justify-center mb-8">
          {sampleQuestions.map((q, i) => (
            <button
              key={i}
              onClick={() => setAskSpEdKitQuery(q)}
              className="text-sm bg-white border border-[#6366F1]/20 text-text-secondary hover:border-[#6366F1]/50 hover:text-[#6366F1] px-4 py-2 rounded-full transition-all"
              data-testid={`sample-question-${i}`}
            >
              {q}
            </button>
          ))}
        </div>
      )}
      
      {/* Error State */}
      {askSpEdKitError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl mb-6 text-center">
          {askSpEdKitError}
        </div>
      )}
      
      {/* Loading State */}
      {askSpEdKitLoading && (
        <div className="bg-white rounded-2xl p-8 shadow-card text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#6366F1] mx-auto mb-4" />
          <p className="text-text-secondary font-body">Researching your question...</p>
          <p className="text-text-muted text-sm mt-2">Consulting educational journals and research</p>
        </div>
      )}
      
      {/* Response */}
      {askSpEdKitResponse && (
        <div className="bg-white rounded-2xl shadow-card overflow-hidden" data-testid="ask-spedkit-response">
          {/* Answer Header */}
          <div className="bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-white">SpEdKit&apos;s Answer</h3>
                <p className="text-white/70 text-sm">Based on educational research</p>
              </div>
            </div>
          </div>
          
          {/* Main Answer */}
          <div className="p-6">
            <div className={`font-body text-text-primary leading-relaxed ${!showFullAnswer ? 'line-clamp-4' : ''}`}>
              {askSpEdKitResponse.response?.answer || 'No answer available'}
            </div>
            {askSpEdKitResponse.response?.answer?.length > 300 && (
              <button
                onClick={() => setShowFullAnswer(!showFullAnswer)}
                className="text-[#6366F1] font-medium text-sm mt-2 flex items-center gap-1 hover:underline"
              >
                {showFullAnswer ? (
                  <>Show less <ChevronUp className="w-4 h-4" /></>
                ) : (
                  <>Read more <ChevronDown className="w-4 h-4" /></>
                )}
              </button>
            )}
            
            {/* Key Points */}
            {askSpEdKitResponse.response?.key_points?.length > 0 && (
              <div className="mt-6">
                <h4 className="font-heading font-semibold text-text-primary mb-3">Key Points</h4>
                <ul className="space-y-2">
                  {askSpEdKitResponse.response.key_points.map((point, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-[#6366F1] rounded-full mt-2 flex-shrink-0" />
                      <span className="font-body text-text-secondary">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Practical Tip */}
            {askSpEdKitResponse.response?.practical_tip && (
              <div className="mt-6 bg-accent/10 border border-accent/20 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Lightbulb className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-heading font-semibold text-accent mb-1">Practical Tip</h4>
                    <p className="font-body text-text-secondary text-sm">{askSpEdKitResponse.response.practical_tip}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Citations */}
            {askSpEdKitResponse.response?.citations?.length > 0 && (
              <div className="mt-6 border-t border-gray-100 pt-6">
                <h4 className="font-heading font-semibold text-text-primary mb-3 flex items-center gap-2">
                  <Quote className="w-4 h-4" />
                  Sources & Citations
                </h4>
                <div className="space-y-3">
                  {askSpEdKitResponse.response.citations.map((citation, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-3">
                      <p className="font-body text-sm text-text-primary font-medium">{citation.source}</p>
                      {citation.relevance && (
                        <p className="font-body text-xs text-text-muted mt-1">{citation.relevance}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Related Topics */}
            {askSpEdKitResponse.response?.related_topics?.length > 0 && (
              <div className="mt-6">
                <h4 className="font-heading font-semibold text-text-muted text-sm mb-2">Related Topics</h4>
                <div className="flex flex-wrap gap-2">
                  {askSpEdKitResponse.response.related_topics.map((topic, i) => (
                    <span 
                      key={i}
                      className="text-xs bg-gray-100 text-text-secondary px-3 py-1 rounded-full"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
            <p className="text-xs text-text-muted text-center">
              Answers are sourced from peer-reviewed journals including Exceptional Children, Harvard Educational Review, and more. Always verify with your local guidelines.
            </p>
          </div>
        </div>
      )}
      
      {/* Journals Badge */}
      {!askSpEdKitResponse && !askSpEdKitLoading && (
        <div className="text-center mt-8">
          <p className="text-xs text-text-muted mb-3">Sourced from leading journals including:</p>
          <div className="flex flex-wrap justify-center gap-2">
            {['Exceptional Children', 'Harvard Educational Review', 'Teaching Exceptional Children', 'Review of Educational Research'].map((journal, i) => (
              <span key={i} className="text-xs bg-white/80 text-text-secondary px-3 py-1 rounded-full border border-gray-200">
                {journal}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AskSpEdKit;
